// src/pages/institution-head/InstitutionHeadProfile.js

import React, { useState, useEffect } from 'react';
import {
    Container,
    Paper,
    Typography,
    Box,
    Avatar,
    Button,
    TextField,
    Grid,
    Divider,
    IconButton,
    Alert,
    CircularProgress,
    Card,
    CardContent,
    Chip,
    LinearProgress,
    Fade,
    Zoom,
    Tooltip,
    useTheme,
    useMediaQuery,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    InputAdornment
} from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import {
    Edit,
    Save,
    Cancel,
    PhotoCamera,
    School,
    Email,
    Phone,
    Flag,
    Badge,
    Description,
    CheckCircle,
    Warning,
    Timeline,
    Dashboard,
    Person,
    Work,
    VerifiedUser,
    Delete,
    Info,
    Lock,
    Visibility,
    VisibilityOff,
    AccountBalance as InstitutionHeadIcon,
    DeleteForever
} from '@mui/icons-material';
import useCustomToast from '../../hooks/useToast';
import {
    APIInstitutionHeadUpdate,
    APIInstitutionHeadProfileCompletion,
    APIInstitutionHeadProfileById,
    APIInstitutionHeadUpdatePassword,
    APIInstitutionHeadProfileImage,
    APIInstitutionHeadDelete
} from '../../apiServices/APIs';
import { authService } from '../../apiServices/authService';
// NOTE: The component file is still named InstitutionHeadBar.js
// but it now renders "Institutional Research Lead Portal" as its visible title.
import InstitutionHeadBar from '../../components/InstitutionHeadBar';
import { apiService } from '../../apiServices/ApiService';
import { getUserDetails, getImageUrl } from '../../utils/helpers';
import LoadingState from '../../components/LoadingState';
import ErrorState from '../../components/ErrorState';
import { useNavigate } from 'react-router-dom';
import { FullWidthContainer } from '../../components/FullWidthContainer';

// Animation keyframes
const pulseAnimation = keyframes`
    0% { transform: scale(1); }
    50% { transform: scale(1.05); }
    100% { transform: scale(1); }
`;

const ProfileAvatar = styled(Avatar)(({ theme }) => ({
    width: 150,
    height: 150,
    margin: '0 auto',
    border: `4px solid ${theme.palette.warning.main}`,
    boxShadow: theme.shadows[3],
    transition: 'all 0.3s ease',
    '&:hover': {
        transform: 'scale(1.02)',
        boxShadow: theme.shadows[6]
    },
    [theme.breakpoints.down('sm')]: {
        width: 120,
        height: 120,
    }
}));

const StyledCard = styled(Card)(({ theme }) => ({
    transition: 'all 0.3s ease',
    height: '100%',
    '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: theme.shadows[8]
    }
}));

const CompletionProgress = styled(Box)(({ theme }) => ({
    position: 'relative',
    marginBottom: theme.spacing(3),
    padding: theme.spacing(2),
    borderRadius: theme.spacing(2),
    background: `linear-gradient(135deg, ${theme.palette.warning.light}15 0%, ${theme.palette.secondary.light}15 100%)`,
}));

// Helper function to get initials from user name
const getInitials = (name) => {
    if (!name) return '?';
    return name
        .split(' ')
        .map(word => word.charAt(0))
        .join('')
        .toUpperCase()
        .substring(0, 2);
};

// Helper function to get avatar color based on user name
const stringToColor = (string) => {
    if (!string) return '#F59E0B';
    let hash = 0;
    for (let i = 0; i < string.length; i++) {
        hash = string.charCodeAt(i) + ((hash << 5) - hash);
    }
    let color = '#';
    for (let i = 0; i < 3; i++) {
        const value = (hash >> (i * 8)) & 0xFF;
        color += ('00' + value.toString(16)).substr(-2);
    }
    return color;
};

const InstitutionHeadProfile = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const navigate = useNavigate();
    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(false);
    const [openPasswordDialog, setOpenPasswordDialog] = useState(false);
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [showCompletionAlert, setShowCompletionAlert] = useState(false);
    const [deleting, setDeleting] = useState(false);
    
    const [formData, setFormData] = useState({
        full_name: '',
        contact_phone: '',
        nationality: '',
        biography: ''
    });
    
    const [passwordForm, setPasswordForm] = useState({
        current_password: '',
        new_password: '',
        new_password_confirmation: ''
    });
    
    const [selectedImage, setSelectedImage] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [profileImageUrl, setProfileImageUrl] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [profileCompletion, setProfileCompletion] = useState({
        percentage: 0,
        missing_fields: [],
        is_profile_complete: false
    });
    const showToast = useCustomToast();

    useEffect(() => {
        apiService.setToastFunction(showToast);
    }, [showToast]);

    useEffect(() => {
        fetchProfile();
        fetchProfileCompletion();
    }, []);

    const fetchProfile = async () => {
        setLoading(true);
        try {
            const currentUserId = getUserDetails("Id");
            const response = await apiService.show(APIInstitutionHeadProfileById, currentUserId);
            if (response.success) {
                const data = response.data;
                setProfile(data);
                setFormData({
                    full_name: data.full_name || '',
                    contact_phone: data.contact_phone || '',
                    nationality: data.nationality || '',
                    biography: data.biography || ''
                });

                // Set profile image URL
                const imgUrl = getImageUrl(APIInstitutionHeadProfileImage, data.profile_image_path);
                setProfileImageUrl(imgUrl || null);
            }
        } catch (error) {
            showToast('Error', 'Failed to load profile', 'error');
        } finally {
            setLoading(false);
        }
    };

    const fetchProfileCompletion = async () => {
        try {
            const currentUserId = getUserDetails("Id");
            const response = await apiService.show(APIInstitutionHeadProfileCompletion, currentUserId);
            
            if (response.success && response.data) {
                setProfileCompletion(response.data);
                if (response.data.percentage < 80 && !showCompletionAlert) {
                    setShowCompletionAlert(true);
                }
            }
        } catch (error) {
            console.error('Failed to fetch profile completion:', error);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handlePasswordChange = (e) => {
        const { name, value } = e.target;
        setPasswordForm(prev => ({ ...prev, [name]: value }));
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (file.size > 5 * 1024 * 1024) {
                showToast('Error', 'Image size must be less than 5MB', 'error');
                return;
            }
            setSelectedImage(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setImagePreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleRemoveImage = () => {
        setSelectedImage(null);
        setImagePreview(null);
    };

    // Simplified password update without length or complexity checks
    const handleUpdatePassword = async () => {
        if (!passwordForm.current_password) {
            showToast('Error', 'Please enter your current password', 'error');
            return;
        }

        if (!passwordForm.new_password) {
            showToast('Error', 'Please enter a new password', 'error');
            return;
        }

        if (passwordForm.new_password !== passwordForm.new_password_confirmation) {
            showToast('Error', 'New passwords do not match', 'error');
            return;
        }

        setUploading(true);
        try {
            const formDataToSend = new FormData();
            formDataToSend.append('current_password', passwordForm.current_password);
            formDataToSend.append('new_password', passwordForm.new_password);
            formDataToSend.append('updateId', getUserDetails("Id"));
            const result = await apiService.update(APIInstitutionHeadUpdatePassword, formDataToSend);

            if (result.success) {
                showToast('Success', 'Password updated successfully', 'success');
                setOpenPasswordDialog(false);
                setPasswordForm({
                    current_password: '',
                    new_password: '',
                    new_password_confirmation: ''
                });
            } else {
                showToast('Error', result.message || 'Failed to update password', 'error');
            }
        } catch (error) {
            console.error("Password update error:", error);
            showToast('Error', error.response?.data?.message || 'Failed to update password', 'error');
        } finally {
            setUploading(false);
        }
    };

    // Handle account deletion
    const handleDeleteAccount = async () => {
        setDeleting(true);
        try {
            const formDataToSend = new FormData();
            formDataToSend.append('user_id', getUserDetails("Id"));
            formDataToSend.append('updateId', getUserDetails("Id"));

            const result = await apiService.delete(APIInstitutionHeadDelete, formDataToSend);

            if (result && result.success) {
                showToast('Success', 'Account deleted successfully', 'success');
                
                // Clear user session
                sessionStorage.removeItem("userInfo");
                sessionStorage.removeItem("authToken");
                
                // Close dialog
                setOpenDeleteDialog(false);
                
                // Navigate to login page
                navigate('/login');
            } else {
                showToast('Error', result.message || 'Failed to delete account', 'error');
            }
        } catch (error) {
            console.error("Delete account error:", error);
            showToast('Error', error.response?.data?.message || 'Failed to delete account', 'error');
        } finally {
            setDeleting(false);
        }
    };

    const handleSave = async () => {
        setUploading(true);
        try {
            const formDataToSend = new FormData();
            formDataToSend.append('updateId', getUserDetails("Id"));
            
            // Only send fields that exist in database
            if (formData.full_name) formDataToSend.append('full_name', formData.full_name);
            if (formData.contact_phone) formDataToSend.append('contact_phone', formData.contact_phone);
            if (formData.nationality) formDataToSend.append('nationality', formData.nationality);
            if (formData.biography) formDataToSend.append('biography', formData.biography);
            
            if (selectedImage) {
                formDataToSend.append('profile_image', selectedImage);
            }

            const response = await apiService.update(APIInstitutionHeadUpdate, formDataToSend);

            if (response.success) {
                showToast('Success', 'Profile updated successfully', 'success');

                // Update sessionStorage with new user data
                const currentUser = JSON.parse(sessionStorage.getItem("userInfo") || "{}");
                const updatedUser = {
                    ...currentUser,
                    full_name: formData.full_name || currentUser.full_name,
                    email: response.data?.email || currentUser.email,
                    contact_phone: response.data?.contact_phone || currentUser.contact_phone,
                    nationality: response.nationality || currentUser.nationality,
                    profile_image_path: response.data?.profile_image_path || currentUser.profile_image_path
                };
                sessionStorage.setItem("userInfo", JSON.stringify(updatedUser));

                setEditing(false);
                setSelectedImage(null);
                setImagePreview(null);
                // Refresh profile data
                await fetchProfile();
                await fetchProfileCompletion();
            } else {
                showToast('Error', response.message || 'Failed to update profile', 'error');
            }
        } catch (error) {
            console.error("Update error:", error);
            showToast('Error', error.response?.data?.message || 'Failed to update profile', 'error');
        } finally {
            setUploading(false);
        }
    };

    const getCompletionColor = () => {
        const { percentage } = profileCompletion;
        if (percentage >= 80) return 'success';
        if (percentage >= 50) return 'warning';
        return 'error';
    };

    // Get avatar color and initials
    const avatarColor = stringToColor(profile?.full_name || '');
    const initials = getInitials(profile?.full_name || '');

    if (loading) {
        return <LoadingState message="Loading Profile..." variant="research" size="large" fullPage />;
    }

    if (!profile) {
        return <ErrorState error="Failed to load profile" onRetry={fetchProfile} title="Unable to Load Profile" variant="research" fullPage retryText="Retry Loading" />;
    }

    return (
        <>
            <FullWidthContainer>
                <Fade in={true} timeout={800}>
                    <Box>
                        <Box sx={{ backgroundColor: 'white', borderRadius: 3 }}>
                            <InstitutionHeadBar notificationCount={10} />
                            {/* Header Section */}
                            <Paper elevation={2} sx={{ 
                                p: { xs: 2, sm: 3 }, 
                                mb: { xs: 2, sm: 3 }, 
                                borderRadius: 3,
                                background: `linear-gradient(135deg, ${theme.palette.warning.main} 0%, ${theme.palette.warning.dark} 100%)`,
                                color: 'white'
                            }}>
                                <Box sx={{ 
                                    display: 'flex', 
                                    justifyContent: 'space-between', 
                                    alignItems: 'center', 
                                    flexWrap: 'wrap', 
                                    gap: 2,
                                    flexDirection: { xs: 'column', sm: 'row' }
                                }}>
                                    <Box sx={{ textAlign: { xs: 'center', sm: 'left' } }}>
                                        <Typography variant="h4" sx={{ 
                                            fontWeight: 'bold', 
                                            mb: 1,
                                            fontSize: { xs: '1.5rem', sm: '2rem' }
                                        }}>
                                            Welcome, {getUserDetails("userName") || 'Institutional Research Lead'}!
                                        </Typography>
                                        <Typography variant="body2" sx={{ opacity: 0.9, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                            Manage your Institutional Research Lead profile
                                        </Typography>
                                    </Box>
                                    <Box sx={{ 
                                        display: 'flex', 
                                        gap: 1,
                                        justifyContent: { xs: 'center', sm: 'flex-end' },
                                        flexWrap: 'wrap'
                                    }}>
                                        <Chip 
                                            icon={profile?.is_active ? <CheckCircle /> : <Warning />}
                                            label={profile?.is_active ? 'Active Account' : 'Inactive Account'}
                                            sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', '& .MuiChip-icon': { color: 'white' } }}
                                            size={isMobile ? "small" : "medium"}
                                        />
                                        <Chip 
                                            icon={<Badge />}
                                            label={`${profileCompletion.percentage}% Complete`}
                                            sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', '& .MuiChip-icon': { color: 'white' } }}
                                            size={isMobile ? "small" : "medium"}
                                        />
                                        <Chip 
                                            icon={<InstitutionHeadIcon />}
                                            label="Institutional Research Lead"
                                            sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', '& .MuiChip-icon': { color: 'white' } }}
                                            size={isMobile ? "small" : "medium"}
                                        />
                                    </Box>
                                </Box>
                            </Paper>

                            {/* Profile Completion Alert */}
                            {showCompletionAlert && profileCompletion.percentage < 80 && !editing && (
                                <Zoom in={showCompletionAlert}>
                                    <Alert 
                                        severity="warning" 
                                        sx={{ mb: 3, borderRadius: 2 }}
                                        action={
                                            <Button color="inherit" size="small" onClick={() => setEditing(true)}>
                                                Complete Now
                                            </Button>
                                        }
                                        onClose={() => setShowCompletionAlert(false)}
                                    >
                                        <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                            Your profile is only {profileCompletion.percentage}% complete. 
                                            Complete your profile to effectively manage your institution's research activities.
                                        </Typography>
                                    </Alert>
                                </Zoom>
                            )}

                            {/* Profile Completion Progress */}
                            <CompletionProgress>
                                <Box sx={{ 
                                    display: 'flex', 
                                    justifyContent: 'space-between', 
                                    alignItems: 'center', 
                                    mb: 2, 
                                    flexWrap: 'wrap', 
                                    gap: 1,
                                    flexDirection: { xs: 'column', sm: 'row' }
                                }}>
                                    <Typography variant="subtitle1" sx={{ 
                                        fontWeight: 'bold', 
                                        display: 'flex', 
                                        alignItems: 'center', 
                                        gap: 1,
                                        fontSize: { xs: '0.9rem', sm: '1rem' }
                                    }}>
                                        <Timeline color="warning" /> Profile Completion Status
                                    </Typography>
                                    <Typography variant="h5" sx={{ 
                                        fontWeight: 'bold', 
                                        color: `${getCompletionColor()}.main`,
                                        fontSize: { xs: '1.25rem', sm: '1.5rem' }
                                    }}>
                                        {profileCompletion.percentage}%
                                    </Typography>
                                </Box>
                                <LinearProgress 
                                    variant="determinate" 
                                    value={profileCompletion.percentage} 
                                    color={getCompletionColor()}
                                    sx={{ height: 8, borderRadius: 4, mb: 2 }}
                                />
                                {profileCompletion.missing_fields.length > 0 && (
                                    <Box sx={{ mt: 2 }}>
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                            Missing information:
                                        </Typography>
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                            {profileCompletion.missing_fields.map((field, index) => (
                                                <Chip 
                                                    key={index}
                                                    label={field.replace('_', ' ').toUpperCase()}
                                                    size="small"
                                                    icon={<Info />}
                                                    variant="outlined"
                                                    sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}
                                                />
                                            ))}
                                        </Box>
                                    </Box>
                                )}
                            </CompletionProgress>
                        </Box>
                        
                        {/* Main Profile Card */}
                        <Paper elevation={3} sx={{ 
                            p: { xs: 2, sm: 3, md: 4 }, 
                            borderRadius: 3,
                            backgroundColor: 'white'
                        }}>
                            {/* Profile Header */}
                            <Box sx={{ textAlign: 'center', mb: 4 }}>
                                <Box sx={{ position: 'relative', display: 'inline-block' }}>
                                    {imagePreview ? (
                                        <ProfileAvatar 
                                            src={imagePreview} 
                                            alt={profile?.full_name}
                                            sx={{ 
                                                animation: editing ? `${pulseAnimation} 1.5s ease-in-out infinite` : 'none',
                                                cursor: editing ? 'pointer' : 'default'
                                            }}
                                        />
                                    ) : profileImageUrl ? (
                                        <ProfileAvatar 
                                            src={profileImageUrl} 
                                            alt={profile?.full_name}
                                            sx={{ 
                                                animation: editing ? `${pulseAnimation} 1.5s ease-in-out infinite` : 'none',
                                                cursor: editing ? 'pointer' : 'default'
                                            }}
                                        />
                                    ) : (
                                        <ProfileAvatar 
                                            sx={{ 
                                                bgcolor: avatarColor,
                                                animation: editing ? `${pulseAnimation} 1.5s ease-in-out infinite` : 'none',
                                                cursor: editing ? 'pointer' : 'default'
                                            }}
                                        >
                                            <Typography variant="h3" sx={{ fontWeight: 'bold' }}>
                                                {initials}
                                            </Typography>
                                        </ProfileAvatar>
                                    )}
                                    {editing && (
                                        <Box sx={{ position: 'absolute', bottom: 0, right: 0, display: 'flex', gap: 1 }}>
                                            <Tooltip title="Upload Photo">
                                                <IconButton
                                                    component="label"
                                                    sx={{
                                                        backgroundColor: 'warning.main',
                                                        color: 'white',
                                                        '&:hover': { backgroundColor: 'warning.dark' },
                                                        width: { xs: 32, sm: 40 },
                                                        height: { xs: 32, sm: 40 }
                                                    }}
                                                >
                                                    <PhotoCamera fontSize="small" />
                                                    <input
                                                        type="file"
                                                        hidden
                                                        accept="image/jpeg,image/png,image/jpg,image/gif"
                                                        onChange={handleImageChange}
                                                    />
                                                </IconButton>
                                            </Tooltip>
                                            {(selectedImage || profileImageUrl) && (
                                                <Tooltip title="Remove Photo">
                                                    <IconButton
                                                        onClick={handleRemoveImage}
                                                        sx={{
                                                            backgroundColor: 'error.main',
                                                            color: 'white',
                                                            '&:hover': { backgroundColor: 'error.dark' },
                                                            width: { xs: 32, sm: 40 },
                                                            height: { xs: 32, sm: 40 }
                                                        }}
                                                    >
                                                        <Delete fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </Box>
                                    )}
                                </Box>
                                <Typography variant="h5" sx={{ 
                                    mt: 2, 
                                    fontWeight: 'bold',
                                    fontSize: { xs: '1.1rem', sm: '1.25rem' }
                                }}>
                                    {profile?.full_name}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                    Registration ID: {profile?.registration_id}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                    Lead ID: {profile?.head_id_number}
                                </Typography>
                                <Box sx={{ mt: 1, display: 'flex', justifyContent: 'center', gap: 1, flexWrap: 'wrap' }}>
                                    <Chip 
                                        icon={<School />} 
                                        label={profile?.university_name} 
                                        size="small" 
                                        color="warning"
                                        variant="outlined"
                                    />
                                    <Chip 
                                        icon={<InstitutionHeadIcon />} 
                                        label="Institutional Research Lead" 
                                        size="small" 
                                        variant="outlined"
                                    />
                                </Box>
                            </Box>

                            <Divider sx={{ my: 3 }}>
                                <Chip label="Leadership Profile" icon={<Dashboard />} />
                            </Divider>

                            {/* Edit Mode Toggle */}
                            <Box sx={{ 
                                display: 'flex', 
                                justifyContent: 'flex-end', 
                                mb: 3, 
                                gap: 2,
                                flexDirection: { xs: 'column', sm: 'row' },
                                flexWrap: 'wrap'
                            }}>
                                {!editing ? (
                                    <>
                                        <Button
                                            variant="outlined"
                                            color="error"
                                            startIcon={<DeleteForever />}
                                            onClick={() => setOpenDeleteDialog(true)}
                                            sx={{ borderRadius: 2 }}
                                            size={isMobile ? "small" : "medium"}
                                            fullWidth={isMobile}
                                        >
                                            Delete Account
                                        </Button>
                                        <Button
                                            variant="outlined"
                                            startIcon={<Lock />}
                                            onClick={() => setOpenPasswordDialog(true)}
                                            sx={{ borderRadius: 2 }}
                                            size={isMobile ? "small" : "medium"}
                                            fullWidth={isMobile}
                                        >
                                            Change Password
                                        </Button>
                                        <Button
                                            variant="contained"
                                            startIcon={<Edit />}
                                            onClick={() => setEditing(true)}
                                            sx={{ 
                                                borderRadius: 2,
                                                background: `linear-gradient(135deg, ${theme.palette.warning.main} 0%, ${theme.palette.warning.dark} 100%)`,
                                                '&:hover': {
                                                    background: `linear-gradient(135deg, ${theme.palette.warning.dark} 0%, ${theme.palette.warning.darker || '#B45309'} 100%)`,
                                                }
                                            }}
                                            size={isMobile ? "small" : "medium"}
                                            fullWidth={isMobile}
                                        >
                                            Edit Profile
                                        </Button>
                                    </>
                                ) : (
                                    <Box sx={{ 
                                        display: 'flex', 
                                        gap: 2,
                                        width: { xs: '100%', sm: 'auto' }
                                    }}>
                                        <Button
                                            variant="outlined"
                                            color="error"
                                            startIcon={<Cancel />}
                                            onClick={() => {
                                                setEditing(false);
                                                setSelectedImage(null);
                                                setImagePreview(null);
                                                fetchProfile();
                                            }}
                                            disabled={uploading}
                                            size={isMobile ? "small" : "medium"}
                                            sx={{ flex: { xs: 1, sm: 'none' } }}
                                        >
                                            Cancel
                                        </Button>
                                        <Button
                                            variant="contained"
                                            color="success"
                                            startIcon={<Save />}
                                            onClick={handleSave}
                                            disabled={uploading}
                                            size={isMobile ? "small" : "medium"}
                                            sx={{ flex: { xs: 1, sm: 'none' } }}
                                        >
                                            {uploading ? 'Saving...' : 'Save Changes'}
                                        </Button>
                                    </Box>
                                )}
                            </Box>

                            {/* Edit Mode Content */}
                            {editing && (
                                <Grid container spacing={2}>
                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            label="Full Name"
                                            name="full_name"
                                            value={formData.full_name}
                                            onChange={handleInputChange}
                                            sx={{ mb: 3 }}
                                            size={isMobile ? "small" : "medium"}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <Person color="action" />
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                        <TextField
                                            fullWidth
                                            label="Email Address"
                                            name="email"
                                            type="email"
                                            value={profile?.email || ''}
                                            disabled
                                            sx={{ mb: 3 }}
                                            size={isMobile ? "small" : "medium"}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <Email color="action" />
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                        <TextField
                                            fullWidth
                                            label="Contact Phone"
                                            name="contact_phone"
                                            value={formData.contact_phone}
                                            onChange={handleInputChange}
                                            sx={{ mb: 3 }}
                                            size={isMobile ? "small" : "medium"}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <Phone color="action" />
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                        <TextField
                                            fullWidth
                                            label="Nationality"
                                            name="nationality"
                                            value={formData.nationality}
                                            onChange={handleInputChange}
                                            sx={{ mb: 3 }}
                                            size={isMobile ? "small" : "medium"}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <Flag color="action" />
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                        <TextField
                                            fullWidth
                                            label="Biography"
                                            name="biography"
                                            multiline
                                            rows={4}
                                            value={formData.biography}
                                            onChange={handleInputChange}
                                            placeholder="Tell us about your role and responsibilities as an Institutional Research Lead..."
                                            size={isMobile ? "small" : "medium"}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <Description color="action" />
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                    </Grid>
                                </Grid>
                            )}

                            {/* View Mode Content */}
                            {!editing && (
                                <Grid container spacing={2}>
                                    <Grid item xs={12} md={6}>
                                        <StyledCard variant="outlined">
                                            <CardContent>
                                                <Typography variant="h6" gutterBottom sx={{ 
                                                    display: 'flex', 
                                                    alignItems: 'center', 
                                                    gap: 1,
                                                    fontSize: { xs: '1rem', sm: '1.125rem' }
                                                }}>
                                                    <Person color="warning" /> Personal Information
                                                </Typography>
                                                <Divider sx={{ mb: 2 }} />
                                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                                        <Person fontSize="small" color="action" />
                                                        <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                                            <strong>Full Name:</strong> {profile?.full_name}
                                                        </Typography>
                                                    </Box>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                                        <Email fontSize="small" color="action" />
                                                        <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                                            <strong>Email:</strong> {profile?.email}
                                                        </Typography>
                                                    </Box>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                                        <Phone fontSize="small" color="action" />
                                                        <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                                            <strong>Contact:</strong> {profile?.contact_phone || 'Not provided'}
                                                        </Typography>
                                                    </Box>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                                        <Flag fontSize="small" color="action" />
                                                        <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                                            <strong>Nationality:</strong> {profile?.nationality || 'Not provided'}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </CardContent>
                                        </StyledCard>
                                    </Grid>
                                    
                                    <Grid item xs={12} md={6}>
                                        <StyledCard variant="outlined">
                                            <CardContent>
                                                <Typography variant="h6" gutterBottom sx={{ 
                                                    display: 'flex', 
                                                    alignItems: 'center', 
                                                    gap: 1,
                                                    fontSize: { xs: '1rem', sm: '1.125rem' }
                                                }}>
                                                    <Work color="warning" /> Leadership Information
                                                </Typography>
                                                <Divider sx={{ mb: 2 }} />
                                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                                        <School fontSize="small" color="action" />
                                                        <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                                            <strong>Institution:</strong> {profile?.university_name}
                                                        </Typography>
                                                    </Box>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                                        <Badge fontSize="small" color="action" />
                                                        <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                                            <strong>Status:</strong> 
                                                            <Chip 
                                                                size="small" 
                                                                label={profile?.is_active ? 'Active' : 'Inactive'}
                                                                color={profile?.is_active ? 'success' : 'error'}
                                                                sx={{ ml: 1, fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                                                            />
                                                        </Typography>
                                                    </Box>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                                        <InstitutionHeadIcon fontSize="small" color="action" />
                                                        <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                                            <strong>Role:</strong> Institutional Research Lead
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </CardContent>
                                        </StyledCard>
                                    </Grid>

                                    {profile?.biography && (
                                        <Grid item xs={12}>
                                            <StyledCard variant="outlined">
                                                <CardContent>
                                                    <Typography variant="h6" gutterBottom sx={{ 
                                                        display: 'flex', 
                                                        alignItems: 'center', 
                                                        gap: 1,
                                                        fontSize: { xs: '1rem', sm: '1.125rem' }
                                                    }}>
                                                        <Description color="warning" /> Biography
                                                    </Typography>
                                                    <Divider sx={{ mb: 2 }} />
                                                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.8, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                                        {profile?.biography}
                                                    </Typography>
                                                </CardContent>
                                            </StyledCard>
                                        </Grid>
                                    )}

                                    {/* Account Information */}
                                    <Grid item xs={12}>
                                        <StyledCard variant="outlined" sx={{ bgcolor: 'grey.50' }}>
                                            <CardContent>
                                                <Typography variant="h6" gutterBottom sx={{ 
                                                    display: 'flex', 
                                                    alignItems: 'center', 
                                                    gap: 1,
                                                    fontSize: { xs: '1rem', sm: '1.125rem' }
                                                }}>
                                                    <VerifiedUser color="warning" /> Account Information
                                                </Typography>
                                                <Divider sx={{ mb: 2 }} />
                                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                                                    <Box>
                                                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
                                                            Registration ID
                                                        </Typography>
                                                        <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                                            {profile?.registration_id}
                                                        </Typography>
                                                    </Box>
                                                    <Box>
                                                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
                                                            Lead ID Number
                                                        </Typography>
                                                        <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                                            {profile?.head_id_number}
                                                        </Typography>
                                                    </Box>
                                                    <Box>
                                                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
                                                            Registration Date
                                                        </Typography>
                                                        <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                                            {new Date(profile?.registration_date).toLocaleDateString()}
                                                        </Typography>
                                                    </Box>
                                                    <Box>
                                                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
                                                            Last Login
                                                        </Typography>
                                                        <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                                            {profile?.last_login_at ? new Date(profile?.last_login_at).toLocaleDateString() : 'N/A'}
                                                        </Typography>
                                                    </Box>
                                                    <Box>
                                                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
                                                            Email Verified
                                                        </Typography>
                                                        <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                                            {profile?.email_verified_at ? 
                                                                <><CheckCircle fontSize="small" color="success" /> Yes</> : 
                                                                <><Warning fontSize="small" color="warning" /> No</>
                                                            }
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </CardContent>
                                        </StyledCard>
                                    </Grid>
                                </Grid>
                            )}
                        </Paper>
                    </Box>
                </Fade>

                {/* Change Password Dialog */}
                <Dialog 
                    open={openPasswordDialog} 
                    onClose={() => setOpenPasswordDialog(false)} 
                    maxWidth="sm" 
                    fullWidth
                    PaperProps={{
                        sx: {
                            m: { xs: 1, sm: 2 },
                            width: { xs: 'calc(100% - 16px)', sm: 'auto' }
                        }
                    }}
                >
                    <DialogTitle sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                        Change Password
                    </DialogTitle>
                    <DialogContent>
                        <Box sx={{ pt: 2 }}>
                            <TextField
                                fullWidth
                                type={showPassword ? 'text' : 'password'}
                                label="Current Password"
                                name="current_password"
                                value={passwordForm.current_password}
                                onChange={handlePasswordChange}
                                sx={{ mb: 2 }}
                                size={isMobile ? "small" : "medium"}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton onClick={() => setShowPassword(!showPassword)} edge="end" size={isMobile ? "small" : "medium"}>
                                                {showPassword ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                            <TextField
                                fullWidth
                                type={showConfirmPassword ? 'text' : 'password'}
                                label="New Password"
                                name="new_password"
                                value={passwordForm.new_password}
                                onChange={handlePasswordChange}
                                sx={{ mb: 2 }}
                                size={isMobile ? "small" : "medium"}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)} edge="end" size={isMobile ? "small" : "medium"}>
                                                {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                            <TextField
                                fullWidth
                                type={showConfirmPassword ? 'text' : 'password'}
                                label="Confirm New Password"
                                name="new_password_confirmation"
                                value={passwordForm.new_password_confirmation}
                                onChange={handlePasswordChange}
                                size={isMobile ? "small" : "medium"}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)} edge="end" size={isMobile ? "small" : "medium"}>
                                                {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Box>
                    </DialogContent>
                    <DialogActions sx={{ p: { xs: 2, sm: 3 } }}>
                        <Button onClick={() => setOpenPasswordDialog(false)} size={isMobile ? "small" : "medium"}>
                            Cancel
                        </Button>
                        <Button 
                            onClick={handleUpdatePassword} 
                            variant="contained" 
                            disabled={uploading} 
                            size={isMobile ? "small" : "medium"}
                            sx={{
                                background: `linear-gradient(135deg, ${theme.palette.warning.main} 0%, ${theme.palette.warning.dark} 100%)`,
                                '&:hover': {
                                    background: `linear-gradient(135deg, ${theme.palette.warning.dark} 0%, ${theme.palette.warning.darker || '#B45309'} 100%)`,
                                }
                            }}
                        >
                            {uploading ? <CircularProgress size={24} /> : 'Update Password'}
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Delete Account Confirmation Dialog */}
                <Dialog 
                    open={openDeleteDialog} 
                    onClose={() => setOpenDeleteDialog(false)} 
                    maxWidth="sm" 
                    fullWidth
                    PaperProps={{
                        sx: {
                            m: { xs: 1, sm: 2 },
                            width: { xs: 'calc(100% - 16px)', sm: 'auto' }
                        }
                    }}
                >
                    <DialogTitle sx={{ 
                        textAlign: 'center', 
                        fontSize: { xs: '1rem', sm: '1.25rem' },
                        color: 'error.main'
                    }}>
                        <DeleteForever sx={{ fontSize: 40, display: 'block', mx: 'auto', mb: 1 }} />
                        Delete Account
                    </DialogTitle>
                    <DialogContent>
                        <Typography variant="body1" sx={{ 
                            textAlign: 'center', 
                            mb: 2,
                            fontSize: { xs: '0.875rem', sm: '1rem' }
                        }}>
                            Are you sure you want to delete your account?
                        </Typography>
                        <Alert severity="warning" sx={{ mb: 2 }}>
                            <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                <strong>Warning:</strong> This action is <strong>permanent</strong> and cannot be undone. 
                                All your data, including profile information, institutional leadership records, 
                                and research oversight activities will be permanently deleted.
                            </Typography>
                        </Alert>
                        <Box sx={{ 
                            p: 2, 
                            bgcolor: 'grey.50', 
                            borderRadius: 2,
                            textAlign: 'center'
                        }}>
                            <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                {profile?.full_name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
                                {profile?.email}
                            </Typography>
                            <Box sx={{ mt: 1, display: 'flex', justifyContent: 'center', gap: 1, flexWrap: 'wrap' }}>
                                <Chip 
                                    icon={<School />} 
                                    label={profile?.university_name} 
                                    size="small" 
                                    variant="outlined" 
                                />
                                <Chip 
                                    icon={<Badge />} 
                                    label={`Lead ID: ${profile?.head_id_number}`} 
                                    size="small" 
                                    variant="outlined" 
                                />
                                <Chip 
                                    icon={<InstitutionHeadIcon />} 
                                    label="Institutional Research Lead" 
                                    size="small" 
                                    variant="outlined" 
                                />
                            </Box>
                        </Box>
                    </DialogContent>
                    <DialogActions sx={{ p: { xs: 2, sm: 3 }, gap: 1, flexDirection: { xs: 'column', sm: 'row' } }}>
                        <Button 
                            onClick={() => setOpenDeleteDialog(false)} 
                            variant="outlined"
                            size={isMobile ? "small" : "medium"}
                            fullWidth={isMobile}
                            disabled={deleting}
                        >
                            Cancel
                        </Button>
                        <Button 
                            onClick={handleDeleteAccount} 
                            variant="contained" 
                            color="error"
                            startIcon={deleting ? <CircularProgress size={20} color="inherit" /> : <DeleteForever />}
                            disabled={deleting}
                            size={isMobile ? "small" : "medium"}
                            fullWidth={isMobile}
                        >
                            {deleting ? 'Deleting...' : 'Delete Account'}
                        </Button>
                    </DialogActions>
                </Dialog>
            </FullWidthContainer>
        </>
    );
};

export default InstitutionHeadProfile;